import React, { useState, useEffect } from 'react';

import { withRouter } from 'react-router';
import { connect } from 'react-redux';

import queryString from 'query-string';
import moment from 'moment';

import { isEmpty } from 'lodash';

import BobPage from '../../../components/BobPage';
import TableContainer from './TableContainer';
import Breadcrumb from '../../../components/Breadcrumb/Breadcrumb';
import KpiPanel from '../../../components/KpiPanel';
import BobTimeStampFilter from '../../../components/RuleExceptionsTable/BobTimestampFilter';
import HtmlTooltip from '../../../components/HtmlTooltip/HtmlTooltip';

import {
    renderScoreData,
    renderFaultData
} from '../../../helpers/kpiHelpers';

import Navigation from '../Navigation/Navigation';

import * as ArchitectActions from '../../../actions/architectActions';
import * as DashboardActions from '../../../actions/dashboardActions';
import * as BobActions from '../../../actions/bobActions';

import classes from './SystemPage.module.css';

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_SIZE = 10;

const SystemPage = ({
    activeConfiguration,
    activeNavClient,
    activeNavCompany,
    activeNavSite,
    clearEntityScoresData,
    clearEntitySensorData,
    companyInfo,
    dashboardEndTimeStamp,
    dashboardIsCurrentTimeStamp,
    dashboardStartTimeStamp,
    dashboardTimeframeEnd,
    dashboardTimeframeStart,
    desiredInterval,
    dashboardTimePeriod,
    faultHistory,
    faultHistoryMeta,
    fetchFaultHistory,
    fetchFaultOverlay,
    fetchFaultSummary,
    fetchConfigEntitesBySiteId,
    fetchDashboardData,
    fetchEntityRules,
    fetchEntitySensorData,
    fetchEntitySensors,
    fetchKpiData,
    fetchRawUnits,
    fetchRuleScoreKpis,
    fetchScoringData,
    fetchSystemBySystemId,
    history,
    sensorConfiguration,
    selectedRule,
    selectedRuleSensors,
    selectedSensors,
    selectedWeatherSensors,
    setAllSensors,
    setDashboardEndTimestamp,
    setDashboardStartTimestamp,
    setDashboardCurrentTimestamp,
    setDashboardTimeframeEnd,
    setDashboardTimeframeStart,
    setEntityRules,
    setEntityScoringData,
    setFaultData,
    setFaultHistory,
    setFaultSummary,
    setSelectedRuleSensors,
    setSelectedRule,
    setSelectedSensors,
    setSensorConfiguration,
    setSystemTabIndex,
    systemKpis,
    ...props
}) => {
    const queryValues = queryString.parse(history.location.search);
    const systemId = queryValues.system;

    const [axes, setAxes] = useState([]);
    const [system, setSystem] = useState();
    const [activeTab, setActiveTab] = useState('');

    const [refreshTab, setRefreshTab] = useState('');

    useEffect(() => {
        const { pathname } = history.location;

        if ( !queryValues.appId) queryValues.appId = 'ad356bf2-b9db-4bb5-922e-c3394b1a63e3';
        // set id's if not exist for BOB menu
        if ( queryValues.appId === 'ad356bf2-b9db-4bb5-922e-c3394b1a63e3' ) {
            if ( !queryValues.parentId) queryValues.parentId = '9f215a70-6597-482c-87a4-e515c346d7b3';
            if ( !queryValues.childId) queryValues.childId = '00a75c91-5483-4c84-bb09-eb037f47bdc9'; // Default to Faults view
        }
        if (!queryValues.company) queryValues.company = companyInfo.companyId;

        const queryValuesString = queryString.stringify(queryValues);

        history.push(`${pathname}?${queryValuesString}`);
    }, [companyInfo]);

    useEffect(() => {
        if (!isEmpty(history.location.state)) {
            if (!isEmpty(history.location.state.sensorHistory)) {
                const sensorHistory = history.location.state.sensorHistory;
                const entityId = history.location.state.entityId;

                if (entityId === queryValues.system) {
                    setSelectedSensors(sensorHistory);
                } else {
                    setSelectedSensors([]);
                }
            } else {
                setSelectedSensors([]);
            }
        } else {
            setSelectedSensors([]);
        }
    }, []);

    useEffect(() => {
        return () => {
            setAllSensors([], {});
            setEntityRules([], {});
            setEntityScoringData([]);
            setFaultSummary([], {});
            setFaultHistory([], {});
            setSelectedRuleSensors([]);
            setSelectedRule({});
            setSelectedSensors([]);
            clearEntitySensorData();
            clearEntityScoresData();
            setSensorConfiguration({});
        };
    }, []);

    useEffect(() => {
        const init = async () => {
            fetchSystemBySystemId(queryValues.system).then(result => {
                setSystem(result);
            });
        };

        if (companyInfo.companyId && queryValues.system) init();

    }, [companyInfo, queryValues.system]);

    useEffect(() => {
        const init = async () => {
            await fetchConfigEntitesBySiteId(activeNavSite.id, { page: 1, limit: 1000 });
        };

        if (activeNavSite.id) init();
    }, [activeNavSite]);

    useEffect(() => {
        const init = async () => {
            setFaultData({});
            const queryValues = queryString.parse(history.location.search);
            let promises = [];

            let weatherSensors = '';
            if (!isEmpty(selectedWeatherSensors)) {
                weatherSensors = activeNavSite.address.postalCode + ':' + selectedWeatherSensors.join(':');
            }

            if (!isEmpty(selectedSensors) || !isEmpty(selectedRuleSensors)) {
                promises.push(fetchEntitySensorData(!isEmpty(selectedSensors) ? selectedSensors : selectedRuleSensors, dashboardStartTimeStamp, dashboardEndTimeStamp, desiredInterval, { axes, entityId: queryValues.system, weatherSensors }));
            }

            promises.push(fetchFaultSummary(activeNavCompany.id, queryValues.system, dashboardStartTimeStamp, dashboardEndTimeStamp));

            if (isEmpty(faultHistoryMeta)) {
                const index = getTimeframeIndex(dashboardTimeframeStart, dashboardTimeframeEnd);
                let startTime = moment().local();
                let endTime = moment().local();
    
                if (!dashboardIsCurrentTimeStamp) {
                    startTime = moment(dashboardStartTimeStamp);
                    endTime = moment().local();
                    moment(dashboardStartTimeStamp).add(4, 'hours') >= moment(dashboardEndTimeStamp) ?
                        endTime = moment(endTime)
                        :   
                        endTime = moment(dashboardStartTimeStamp).add(4, 'hours');
                }
    
                setTimeframe(startTime, endTime);
                
                promises.push(fetchFaultHistory(activeNavCompany.id, queryValues.system, moment(startTime).format(), moment(endTime).format(), index));
            }

            promises.push(fetchRuleScoreKpis(activeNavCompany.id, queryValues.system, { From: dashboardStartTimeStamp, To: dashboardEndTimeStamp, DesiredTimeSeriesIntervalInMinutes: desiredInterval, EntityName: 'Systems' }));

            if (!isEmpty(selectedRule)) {
                if (!dashboardIsCurrentTimeStamp) {
                    promises.push(fetchFaultOverlay(activeNavCompany.id, queryValues.system, selectedRule.ruleId, dashboardStartTimeStamp, dashboardEndTimeStamp));
                }
                promises.push(fetchScoringData(activeNavCompany.id, selectedRuleSensors, [selectedRule.ruleId], dashboardStartTimeStamp, dashboardEndTimeStamp, desiredInterval, { SystemId: queryValues.system, weatherSensors }));
            }

            await Promise.all(promises.map(p => p.catch(err => err)));
        };

        if ((activeNavCompany.id && activeNavCompany.id === queryValues.company) || (isEmpty(activeConfiguration) && !isEmpty(sensorConfiguration))) init();
    }, [activeNavCompany.id, queryValues.system, dashboardStartTimeStamp, dashboardEndTimeStamp, desiredInterval, sensorConfiguration]);

    const setTimeframe = (start, end) => {
        Promise.all([
            setDashboardTimeframeStart(moment(start)),
            setDashboardTimeframeEnd(moment(end))
        ].map(p => p.catch(err => err)));
    };


    const getTimeframeIndex = (start, end) => {
        const startDifference = moment.duration(moment(start).seconds(0).milliseconds(0).diff(moment(dashboardStartTimeStamp).seconds(0).milliseconds(0)));

        const startHours = startDifference.asHours();
        let index = startHours / 4 >> 0;

        const diff = moment.duration(moment(end).seconds(0).milliseconds(0).diff(moment(start).seconds(0).milliseconds(0)));
        if (diff.asHours() > 4) index++;

        return index;
    };
    
    const updateTimeStamp  = async (tabView) => {
        if (dashboardTimePeriod === 'Current') {
            const endTimeStamp = moment().local();
            const startTimeStamp = moment().local();

            setDashboardStartTimestamp(startTimeStamp);
            setDashboardEndTimestamp(endTimeStamp);
            setDashboardCurrentTimestamp(moment().local());
        } else if (dashboardTimePeriod === 'Previous') {
            const endTimeStamp = moment().local();
            const interval = tabView && tabView.toLowerCase().includes('score') ? 15 : desiredInterval;
            const minutes = interval === 1 ? 240 : 1440;
            const startTimeStamp = interval >= 5 ?
                moment().set('minute', Math.floor(moment().minute() / interval) * interval).subtract(minutes, 'm').local()
                : moment().subtract(minutes, 'm').local();

            setDashboardStartTimestamp(startTimeStamp);
            setDashboardEndTimestamp(endTimeStamp);
        }
    };

    useEffect(() => {
        const refresh = async () => {
            await updateTimeStamp(refreshTab);
            
            const queryValues = queryString.parse(history.location.search);
            let promises = [];

            let weatherSensors = '';
            if (!isEmpty(selectedWeatherSensors)) {
                weatherSensors = activeNavSite.address.postalCode + ':' + selectedWeatherSensors.join(':');
            }

            if (!isEmpty(selectedSensors)) {
                promises.push(fetchEntitySensorData(selectedSensors, dashboardStartTimeStamp, dashboardEndTimeStamp, desiredInterval, { axes, entityId: queryValues.system, weatherSensors }));
            }

            promises.push(fetchEntityRules(activeNavCompany.id, queryValues.system, dashboardStartTimeStamp, dashboardEndTimeStamp));
            promises.push(fetchRuleScoreKpis(activeNavCompany.id, queryValues.system, { From: dashboardStartTimeStamp, To: dashboardEndTimeStamp, DesiredTimeSeriesIntervalInMinutes: desiredInterval, EntityName: 'Systems' }));

            if (!isEmpty(selectedRule)) {
                if (!dashboardIsCurrentTimeStamp) {
                    promises.push(fetchFaultOverlay(activeNavCompany.id, queryValues.system, selectedRule.ruleId, dashboardStartTimeStamp, dashboardEndTimeStamp));
                }
                promises.push(fetchScoringData(activeNavCompany.id, selectedRuleSensors, [selectedRule.ruleId], dashboardStartTimeStamp, dashboardEndTimeStamp, desiredInterval, { SystemId: queryValues.system, weatherSensors }));
            }

            await Promise.all(promises.map(p => p.catch(err => err)));
        };

        if (refreshTab === 'sensorgraph' || refreshTab === 'sensorgrid' || refreshTab === 'scoregraph' || refreshTab === 'scoregrid') {
            refresh();
            setRefreshTab('');
        } 
    }, [refreshTab]);

    useEffect(() => {
        const init = async () => {

            let promises = [];

            let weatherSensors = '';
            if (!isEmpty(selectedWeatherSensors)) {
                weatherSensors = activeNavSite.address.postalCode + ':' + selectedWeatherSensors.join(':');
            }

            if (!isEmpty(history.location.state)) {
                if (!isEmpty(history.location.state.sensorHistory)) {
                    const sensorHistory = history.location.state.sensorHistory;
                    const entityId = history.location.state.entityId;

                    if (entityId === queryValues.system) {
                        setSelectedSensors(sensorHistory);
                        setSelectedRuleSensors(sensorHistory);

                        promises.push(fetchEntitySensorData(sensorHistory, dashboardStartTimeStamp, dashboardEndTimeStamp, desiredInterval, { axes, entityId: queryValues.system, weatherSensors  }));
                    }
                }
                if (!isEmpty(history.location.state.ruleHistory)) {
                    const ruleHistory = history.location.state.ruleHistory;
                    const sensorHistory = history.location.state.sensorHistory;

                    setSelectedRule(ruleHistory);
                    promises.push(fetchScoringData(queryValues.company, sensorHistory, [ruleHistory.ruleId], dashboardStartTimeStamp, dashboardEndTimeStamp, desiredInterval, { SystemId: queryValues.system, weatherSensors }));
                }
                let state = { ...history.location.state };
                delete state.ruleHistory;
                delete state.sensorHistory;
                history.replace({ ...history.location, state });
            }

            await Promise.all(promises.map(p => p.catch(err => err)));
        };

        if (companyInfo.companyId) init();
    }, [companyInfo]);

    useEffect(() => {
        const init = async () => {
            await Promise.all([
                fetchEntitySensors(activeNavSite.id, queryValues.system, { mutateCompanyId: true }),
                fetchDashboardData(activeNavCompany.id, 'SystemEquipment', { clientId: activeNavClient.id, siteId: activeNavSite.id, systemId, sort: 'equipmentName^asc', page: DEFAULT_PAGE, limit: DEFAULT_PAGE_SIZE })
            ].map(p => p.catch(err => err)));
        };

        if (activeNavSite.id && queryValues.system) init();
    }, [activeNavSite]);

    useEffect(() => {
        const init = async () => {
            await fetchKpiData(activeNavCompany.id, 'System', systemId, dashboardStartTimeStamp, dashboardEndTimeStamp);
        };

        if (activeNavCompany.id) init();
    }, [activeNavCompany.id, dashboardEndTimeStamp, dashboardStartTimeStamp]);

    const buildEntityNameTooltip = props => {
        return (
            <div>
                <div className="entityTooltipTitle">System Information</div>
                <div className="entityTooltipHeader">Site</div>
                <div className="entityTooltipDetail">{activeNavSite.name}</div>
                <div className="entityTooltipHeader">System Type</div>
                <div className="entityTooltipDetail">{props.systemType ? props.systemType.name : ''}</div>
            </div>
        );
    };

    return (
        <BobPage>
            <div className={classes.SystemPage}>
                <div className={classes.BreadcrumbPanel}>
                    <Breadcrumb system={system} />
                </div>
                <div className={classes.HeaderPanel}>
                    <div className={classes.NavigationPanel}>
                        <Navigation />
                    </div>
                    <div className={classes.Date}>
                        {
                            activeTab === 'Health Monitor' ?
                                null
                                :
                                <BobTimeStampFilter
                                    className={classes.TimestampOverride}
                                    maxDate={moment().local()}
                                    disableIntervalSelector={activeTab === 'Sensors' ? false : true}
                                />
                        }
                    </div>
                </div>
                <div className={classes.TitlePanel}>
                    <div className={classes.Name}>
                        { system ? system.name + ' ': 'System Overview' }
                        {system ?
                            <HtmlTooltip
                                information={buildEntityNameTooltip(system)}
                                placement={'right-start'}
                                body={
                                    <div className={'infoIcon'}><span className="material-icons-outlined md-16" style={{ cursor: 'default' }}>info</span></div>
                                }
                            />
                            : null
                        }
                    </div>
                </div>
                {
                    process.env.dashboardKpiEnabled ?
                        <div className={classes.DataPanel}>
                            <KpiPanel
                                title="Score (% Change)"
                                data={renderScoreData(systemKpis, 'System', dashboardIsCurrentTimeStamp)}
                                type="score"
                                className={classes.PercentChange}
                                information="System Score Information"
                            />
                            <KpiPanel
                                title="Faults"
                                data={renderFaultData(systemKpis)}
                                kpiType={'fault'}
                            />
                        </div>
                        :
                        null
                }
                <div className={classes.TablePanel}>
                    <TableContainer
                        activeTab={activeTab}
                        axes={axes}
                        refreshTab={refreshTab}
                        setRefreshTab={(tab) => setRefreshTab(tab)}
                        setAxes={setAxes}
                        setActiveTab={setActiveTab}
                        system={system}
                        updateTimeStamp={updateTimeStamp}
                    />
                </div>
            </div>
        </BobPage>
    );
};

const mapStateToProps = state => {
    return {
        activeNavClient:             state.bob.activeNavClient,
        activeNavCompany:            state.bob.activeNavCompany,
        activeNavSite:               state.bob.activeNavSite,
        companyInfo:                 state.companyInfo,
        dashboardTimeframeEnd:       state.dashboard.dashboardTimeframeEnd ? state.dashboard.dashboardTimeframeEnd.format() : null,
        dashboardTimeframeStart:     state.dashboard.dashboardTimeframeStart ? state.dashboard.dashboardTimeframeStart.format() : null,
        dashboardEndTimeStamp:       state.dashboard.dashboardEndTimeStamp ? state.dashboard.dashboardEndTimeStamp.format() : null,
        dashboardIsCurrentTimeStamp: state.dashboard.dashboardIsCurrentTimeStamp,
        dashboardStartTimeStamp:     state.dashboard.dashboardStartTimeStamp ? state.dashboard.dashboardStartTimeStamp.format() : null, 
        dashboardTimePeriod:         state.dashboard.dashboardTimePeriod,
        desiredInterval:             state.dashboard.desiredInterval,
        faultHistory:                state.bob.faultHistory,
        faultHistoryMeta:            state.bob.faultHistoryMeta,
        selectedRule:                state.dashboard.selectedRule,
        selectedRuleSensors:         state.dashboard.selectedRuleSensors,
        selectedSensors:             state.dashboard.selectedSensors,
        selectedWeatherSensors:      state.dashboard.selectedWeatherSensors,
        systemKpis:                  state.dashboard.systemKpis,
        sensorConfiguration:         state.dashboard.sensorConfiguration,
        systemTabIndex:              state.dashboard.systemTabIndex
    };
};


const mapDispatchToProps = {
    ...ArchitectActions,
    ...BobActions,
    ...DashboardActions
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(SystemPage));
